import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import { Review } from '../types/firestore';

interface SubmitVerifiedReviewRequest {
  requestId: string;
  rating: number;
  comment: string;
}

interface SubmitVerifiedReviewResponse {
  success: boolean;
  reviewId: string;
}

interface AddReviewInput {
  requestId?: string;
  repairRequestId?: string;
  rating: number;
  comment: string;
  clientId?: string;
  serviceProviderId?: string;
}

const MAX_PUBLIC_SERVICE_REVIEWS = 150;

const isHalfStepRating = (value: number): boolean => {
  if (!Number.isFinite(value) || value < 1 || value > 5) {
    return false;
  }
  const doubled = value * 2;
  return Math.abs(doubled - Math.round(doubled)) < Number.EPSILON;
};

const mapReviewError = (error: any): string => {
  const code = String(error?.code || '');

  if (code.includes('unauthenticated')) {
    return 'Musisz być zalogowany, aby wystawić opinię.';
  }
  if (code.includes('permission-denied')) {
    return 'Możesz wystawić opinię tylko do własnego, zakończonego zlecenia.';
  }
  if (code.includes('already-exists')) {
    return 'Opinia dla tego zlecenia została już wystawiona.';
  }
  if (code.includes('failed-precondition')) {
    return 'Opinię można wystawić dopiero po zakończeniu zlecenia.';
  }
  if (code.includes('invalid-argument')) {
    return 'Sprawdź ocenę i treść opinii, a następnie spróbuj ponownie.';
  }

  return 'Nie udało się zapisać opinii. Spróbuj ponownie za chwilę.';
};

const addReview = async (reviewData: AddReviewInput): Promise<string> => {
  const requestId = String(reviewData.requestId || reviewData.repairRequestId || '').trim();
  const rating = Number(reviewData.rating);
  const comment = String(reviewData.comment || '').trim();

  if (!requestId) {
    throw new Error('Nieprawidłowe ID zlecenia.');
  }
  if (!isHalfStepRating(rating)) {
    throw new Error('Ocena musi być z zakresu 1-5 (co 0.5).');
  }
  if (comment.length < 3) {
    throw new Error('Komentarz powinien mieć co najmniej 3 znaki.');
  }

  try {
    const submitReviewFn = httpsCallable<SubmitVerifiedReviewRequest, SubmitVerifiedReviewResponse>(
      functions,
      'submitVerifiedReview'
    );

    const result = await submitReviewFn({
      requestId,
      rating,
      comment,
    });

    if (!result.data?.success || !result.data?.reviewId) {
      throw new Error('Nie udało się zapisać opinii.');
    }

    return result.data.reviewId;
  } catch (error: any) {
    console.error('Błąd podczas wystawiania opinii:', error);
    throw new Error(mapReviewError(error));
  }
};

const getReviewForRequest = async (requestId: string): Promise<Review | null> => {
  const normalizedRequestId = String(requestId || '').trim();
  if (!normalizedRequestId) {
    return null;
  }

  const reviewRef = doc(firestore, 'reviews', normalizedRequestId);
  const reviewSnap = await getDoc(reviewRef);
  if (!reviewSnap.exists()) {
    // Legacy fallback (older data model with random review document IDs).
    const reviewsRef = collection(firestore, 'reviews');
    const byRequestIdQuery = query(
      reviewsRef,
      where('requestId', '==', normalizedRequestId),
      limit(1)
    );
    const byRequestIdSnap = await getDocs(byRequestIdQuery);
    if (!byRequestIdSnap.empty) {
      const legacyDoc = byRequestIdSnap.docs[0];
      return { id: legacyDoc.id, ...legacyDoc.data() } as Review;
    }

    const byLegacyRepairRequestIdQuery = query(
      reviewsRef,
      where('repairRequestId', '==', normalizedRequestId),
      limit(1)
    );
    const byLegacyRepairRequestIdSnap = await getDocs(byLegacyRepairRequestIdQuery);
    if (!byLegacyRepairRequestIdSnap.empty) {
      const legacyDoc = byLegacyRepairRequestIdSnap.docs[0];
      return { id: legacyDoc.id, ...legacyDoc.data() } as Review;
    }

    return null;
  }

  return { id: reviewSnap.id, ...reviewSnap.data() } as Review;
};

const getReviewsForService = async (
  serviceProviderId: string,
  maxItems = 50
): Promise<Review[]> => {
  const normalizedServiceId = String(serviceProviderId || '').trim();
  if (!normalizedServiceId) {
    return [];
  }

  const reviewsRef = collection(firestore, 'reviews');
  const reviewsQuery = query(
    reviewsRef,
    where('serviceProviderId', '==', normalizedServiceId),
    orderBy('createdAt', 'desc'),
    limit(Math.max(1, Math.min(maxItems, MAX_PUBLIC_SERVICE_REVIEWS)))
  );

  const querySnapshot = await getDocs(reviewsQuery);
  return querySnapshot.docs.map((docSnapshot) => {
    return { id: docSnapshot.id, ...docSnapshot.data() } as Review;
  });
};

export const reviewService = {
  addReview,
  getReviewForRequest,
  getReviewsForService,
};
